// Outreach Types — LinkedIn Outreach Tracking System (Web)

import type { LucideIcon } from "lucide-react"
import {
  Send, XCircle, CheckCircle, MessageSquare, MessageCircle,
  RefreshCw, Star, Ban, TrendingUp, Circle, Eye, Mail, MailOpen, PenLine,
} from "lucide-react"

export type OutreachStatus =
  | "not_contacted"
  | "invite_sent"
  | "invite_ignored"
  | "connected"
  | "msg_sent"
  | "replied"
  | "follow_up"
  | "qualified"
  | "not_interested"
  | "opportunity"

export type OutreachEventType =
  | "invite_sent"
  | "invite_accepted"
  | "message_sent"
  | "message_received"
  | "profile_viewed"
  | "inmail_sent"
  | "inmail_received"
  | "follow_up"
  | "manual_note"

export const OUTREACH_STATUS_CONFIG: Record<OutreachStatus, { title: string; color: string; icon: LucideIcon }> = {
  not_contacted: { title: "Nao Contatado", color: "#94a3b8", icon: Circle },
  invite_sent: { title: "Convite Enviado", color: "#3b82f6", icon: Send },
  invite_ignored: { title: "Convite Ignorado", color: "#ef4444", icon: XCircle },
  connected: { title: "Conectado", color: "#22c55e", icon: CheckCircle },
  msg_sent: { title: "Msg Enviada", color: "#8b5cf6", icon: MessageSquare },
  replied: { title: "Respondeu", color: "#f59e0b", icon: MessageCircle },
  follow_up: { title: "Follow-up", color: "#06b6d4", icon: RefreshCw },
  qualified: { title: "Qualificado", color: "#10b981", icon: Star },
  not_interested: { title: "Sem Interesse", color: "#6b7280", icon: Ban },
  opportunity: { title: "Oportunidade", color: "#f97316", icon: TrendingUp },
}

export const EVENT_TYPE_CONFIG: Record<OutreachEventType, { title: string; icon: LucideIcon; color: string }> = {
  invite_sent: { title: "Convite Enviado", icon: Send, color: "#3b82f6" },
  invite_accepted: { title: "Convite Aceito", icon: CheckCircle, color: "#22c55e" },
  message_sent: { title: "Mensagem Enviada", icon: MessageSquare, color: "#8b5cf6" },
  message_received: { title: "Mensagem Recebida", icon: MessageCircle, color: "#f59e0b" },
  profile_viewed: { title: "Perfil Visualizado", icon: Eye, color: "#06b6d4" },
  inmail_sent: { title: "InMail Enviado", icon: Mail, color: "#3b82f6" },
  inmail_received: { title: "InMail Recebido", icon: MailOpen, color: "#f59e0b" },
  follow_up: { title: "Follow-up", icon: RefreshCw, color: "#06b6d4" },
  manual_note: { title: "Nota Manual", icon: PenLine, color: "#6b7280" },
}

export interface OutreachEvent {
  id: string
  user_id: string
  contact_id: string | null
  lead_id: string | null
  event_type: OutreachEventType
  channel: string
  direction: string
  subject: string | null
  body: string | null
  linkedin_profile_url: string | null
  metadata: Record<string, unknown> | null
  created_at: string
  // Enriched contact/lead fields
  contact_name: string | null
  contact_job_title: string | null
  contact_linkedin_url: string | null
  lead_company: string | null
  lead_title: string | null
}

export interface OutreachLeadSummary {
  lead_id: string
  outreach_status: OutreachStatus
  outreach_owner_id: string | null
  last_outreach_action_at: string | null
  next_followup_at: string | null
  cadence_name: string | null
  cadence_step: number
  total_steps: number
  recent_events: OutreachEvent[]
  connection_status: string | null
}

export interface OutreachMetrics {
  period_start: string
  period_end: string
  invites_sent: number
  invites_accepted: number
  messages_sent: number
  messages_received: number
  profiles_viewed: number
  leads_created: number
  opps_created: number
  acceptance_rate: number
  reply_rate: number
}

export interface TeamMetrics {
  team_metrics: OutreachMetrics[]
  user_breakdown: {
    user_id: string
    user_name: string
    invites_sent: string
    invites_accepted: string
    messages_sent: string
    messages_received: string
    leads_created: string
  }[]
}

export interface ConversionFunnel {
  invite_sent: number
  connected: number
  msg_sent: number
  replied: number
  qualified: number
  opportunity: number
  conversion_rates: Record<string, number>
}

export interface QueueItem {
  lead_id: string
  contact_id: string | null
  company_name: string
  contact_name: string | null
  contact_job_title: string | null
  contact_linkedin_url: string | null
  lead_title: string | null
  outreach_status: OutreachStatus
  next_action: string
  next_followup_at: string | null
  cadence_name: string | null
  cadence_step: number | null
  days_overdue: number
  is_owner: boolean
  has_contact: boolean
  contact_whatsapp: string | null
}

export interface QueueResponse {
  items: QueueItem[]
  total: number
  overdue: number
  page: number
  per_page: number
  total_pages: number
}

export interface OutreachCadence {
  id: string
  name: string
  description: string | null
  owner_id: string
  is_active: boolean
  is_global: boolean
  total_steps: number
  created_at: string
  steps: OutreachCadenceStep[]
}

export interface OutreachCadenceStep {
  id: string
  cadence_id: string
  step_order: number
  action_type: string
  delay_days: number
  message_template: string | null
  ai_personalize: boolean
}

export interface OutreachConfig {
  linkedin_email: string | null
  linkedin_profile_url: string | null
  imap_enabled: boolean
  email_integration_mode: string
  imap_host: string | null
  imap_port: number
  imap_username: string | null
}

export interface SuggestedMessage {
  message: string
  context: string | null
}

export interface CsvImportRecord {
  id: string
  user_id: string
  filename: string
  total_rows: number
  new_contacts: number
  updated_contacts: number
  new_leads: number
  skipped_rows: number
  errors: Record<string, unknown> | null
  status: string
  created_at: string
}

// ── Outreach Intelligence Types (Fase 1) ──

export interface GeneratedMessages {
  linkedin: { text: string; char_count: number; limit: number }
  email: { subject: string; body: string }
  whatsapp: { text: string; char_count: number; limit: number }
  whatsapp_wa_url?: string
}

export interface AIInstructions {
  global_instructions: string
  tone: string
  include_social_proof: boolean
}

export interface AIKnowledgeItem {
  id: string
  filename: string
  category: string
  size_bytes?: number
  created_at: string
}
